import { Box, Paper, Grid, Stack, CircularProgress } from "@mui/material";
import {
  AccessTime as ClockIcon,
  Today as CalendarIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  AttachMoney as DollarIcon,
  Person as UserIcon,
  EmojiEvents as AwardIcon,
  Warning as AlertTriangleIcon,
  Info as InfoIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import { Text, Title } from "@clipboard-health/ui-react";
import { useGetShifts } from "@src/appV2/Shifts/api/useGetShifts";
import { isDefined } from "@clipboard-health/util-ts";
import { API_TYPES } from "@src/appV2/api/apiTypes";
import { ShiftLogSchema } from "@src/appV2/ShiftLogs/api/schema";
import { Shift } from "@src/interface";

import { roundUpAndFormatRateWithZeroes } from "@src/utils/numbers";
import { ShiftLogAction } from "@src/appV2/ShiftLogs/api/ShiftLogAction";
import { TimelineEvent } from "./type";
import { KeyMetricsAccordion } from "./KeyMetricsAccordion";
import { ShiftMoreDetailsAccordion } from "./ShiftDetailsAccordion";
import { ShiftSummary } from "./ShiftSummary";
import { ShiftTimeline } from "./ShiftTimeline";

const getTimelineEventFromLog = (log: ShiftLogSchema, index: number): TimelineEvent | null => {
  const { actionDetails = {}, eventAt, workerName } = log.attributes ?? {};
  const {
    action,
    subAction,
    notes,
    reason,
    performedBy: { name: performedBy } = {},
  } = actionDetails;

  switch (action) {
    case ShiftLogAction.SHIFT_OPEN:
      return {
        id: index,
        title: "Shift Created",
        time: new Date(eventAt ?? ""),
        description: performedBy ? `Created by ${performedBy}` : "Shift created by facility",
        icon: <CalendarIcon sx={{ color: "#1976d2" }} />,
        type: "creation",
        details: {
          performedBy,
          notes: notes?.[0],
        },
      };

    case ShiftLogAction.SHIFT_ASSIGN:
      return {
        id: index,
        title: `Assigned to ${workerName || "worker"}`,
        time: new Date(eventAt ?? ""),
        description: performedBy ? `Assigned by ${performedBy}` : "Assigned by admin",
        icon: <CheckIcon sx={{ color: "#2e7d32" }} />,
        type: "booked",
        action,
        subAction,
        details: {
          performedBy,
          notes: notes?.[0],
        },
      };

    case ShiftLogAction.SHIFT_CLAIM:
      return {
        id: index,
        title: `Booked by ${workerName || "worker"}`,
        time: new Date(eventAt ?? ""),
        description: "Shift claimed by worker",
        icon: <CheckIcon sx={{ color: "#2e7d32" }} />,
        type: "booked",
        action,
        subAction,
        details: {
          workerName,
          selfClaimed: true,
        },
      };

    case ShiftLogAction.SHIFT_VERIFY:
      return {
        id: index,
        title: "Shift verified",
        time: new Date(eventAt ?? ""),
        description: performedBy ? `Verified by ${performedBy}` : "Shift is verified",
        icon: <CheckIcon sx={{ color: "#2e7d32" }} />,
        type: "clockout",
        action,
        subAction,
        details: {
          performedBy,
          notes: notes?.[0],
        },
      };

    case ShiftLogAction.SHIFT_PAY_RATE_CHANGE:
      return {
        id: index,
        title: `Pay Rate Change`,
        time: new Date(eventAt ?? ""),
        description: notes?.[0] ?? "Pay rate changes",
        icon: <DollarIcon sx={{ color: "#2e7d32" }} />,
        type: "rate",
        action,
        subAction,
        details: {
          performedBy,
          notes: notes?.[0],
        },
      };

    case ShiftLogAction.MARKED_AS_NON_URGENT:
      return {
        id: index,
        title: "Priority Status Changed",
        time: new Date(eventAt ?? ""),
        description: "Shift marked as non-urgent",
        icon: <AlertTriangleIcon sx={{ color: "#ed6c02" }} />,
        type: "note",
        action,
        subAction,
      };

    case ShiftLogAction.WORKER_CANCEL:
    case ShiftLogAction.FACILITY_CANCEL:
    case ShiftLogAction.FACILITY_CANCEL_FILLED:
    case ShiftLogAction.FACILITY_CANCELLED_ME:
      return {
        id: index,
        title: "Shift Cancelled",
        time: new Date(eventAt ?? ""),
        description:
          action === ShiftLogAction.WORKER_CANCEL
            ? `Cancelled by ${workerName ?? "worker"}`
            : performedBy
            ? `Cancelled by ${performedBy}`
            : "Cancelled by facility",
        icon: <CancelIcon sx={{ color: "#d32f2f" }} />,
        type: "cancel",
        action,
        subAction,
        details: {
          reason: reason?.description,
          performedBy,
          notes: notes?.[0],
        },
      };

    case ShiftLogAction.SHIFT_UNASSIGN:
      return {
        id: index,
        title: "Worker Unassigned",
        time: new Date(eventAt ?? ""),
        description: `${workerName ?? "Worker"} was unassigned from this shift`,
        icon: <UserIcon sx={{ color: "#f57c00" }} />,
        type: "unassign",
        action,
        subAction,
        details: {
          workerName,
          reason: reason?.description,
          performedBy,
          notes: notes?.[0],
        },
      };

    case ShiftLogAction.SHIFT_NOTE_UPDATE:
      return {
        id: index,
        title: "Notes Updated",
        time: new Date(eventAt ?? ""),
        description: performedBy ? `Updated by ${performedBy}` : "Shift notes were updated",
        icon: <InfoIcon sx={{ color: "#1976d2" }} />,
        type: "note",
        action,
        subAction,
        details: {
          performedBy,
          notes: notes?.[0],
        },
      };

    case ShiftLogAction.SHIFT_TIME_CHANGE:
      return {
        id: index,
        title: "Shift Time Updated",
        time: new Date(eventAt ?? ""),
        description: performedBy ? `Updated by ${performedBy}` : "Shift time was updated",
        icon: <ScheduleIcon sx={{ color: "#1976d2" }} />,
        type: "note",
        action,
        subAction,
        details: {
          performedBy,
          notes: notes?.[0],
        },
      };

    default:
      return null;
  }
};

const mapShiftDataToTimelineEvents = (
  shift: Shift,
  shiftLogs: ShiftLogSchema[]
): TimelineEvent[] => {
  const mappedEvents: TimelineEvent[] = [];

  // Priority events
  if (shift.priorityTill) {
    mappedEvents.push({
      id: 1,
      title: "Opened to High Priority Users",
      time: shift.createdAt ?? new Date(),
      description: "Shift opened to premium users",
      icon: <AwardIcon sx={{ color: "#9c27b0" }} />,
      type: "priority",
      details: {
        priorityTill: shift.priorityTill,
      },
    });
  }

  // Magic shift marker if applicable
  if (shift.isMagicShift) {
    mappedEvents.push({
      id: 2,
      title: "Magic Shift Activated",
      time: shift.createdAt ?? new Date(),
      description: "Enhanced shift visibility and incentives",
      icon: <AwardIcon sx={{ color: "#673ab7" }} />,
      type: "magic",
    });
  }

  if (isDefined(shift.shiftSpecifications) && shift.shiftSpecifications?.length > 0) {
    mappedEvents.push({
      id: 3,
      title: "Shift Specifications Added",
      time: shift.createdAt ?? new Date(),
      description: "Special requirements for this shift",
      icon: <AssignmentIcon sx={{ color: "#0288d1" }} />,
      type: "shift_spec",
    });
  }
  if (shift.clockInOut?.start) {
    mappedEvents.push({
      id: 4,
      title: "Clocked In",
      time: new Date(shift.clockInOut?.start),
      description: shift.agent?.name ? `${shift.agent.name} clocked in` : "Agent clocked in",
      icon: <ClockIcon sx={{ color: "#ed6c02" }} />,
      type: "clockin",
      details: {
        agentName: shift.agent?.name,
      },
    });
  }

  if (shift.clockInOut?.end) {
    mappedEvents.push({
      id: 5,
      title: "Clocked Out",
      time: new Date(shift.clockInOut?.end),
      description: shift.agent?.name ? `${shift.agent.name} clocked out` : "Agent clocked out",
      icon: <ClockIcon sx={{ color: "#ed6c02" }} />,
      type: "clockout",
      details: {
        agentName: shift.agent?.name,
        duration: shift.time,
      },
    });
  }

  if (shift.instantPayDetails) {
    const { payments, is100InstantPayEnabled, paidAmount } = shift.instantPayDetails;

    if (payments?.length > 0) {
      payments.forEach((payment, idx) => {
        mappedEvents.push({
          id: 100 + idx,
          title: `Payment ${idx + 1} Processed`,
          time: new Date(shift.clockInOut?.end ?? shift.createdAt ?? ""),
          description: `$${roundUpAndFormatRateWithZeroes(payment.amount)} paid to worker`,
          icon: <DollarIcon sx={{ color: "#2e7d32" }} />,
          type: "payment",
          details: {
            amount: payment.amount,
            isPaidInstantly: is100InstantPayEnabled,
            paidAmount,
          },
        });
      });
    } else if (is100InstantPayEnabled && paidAmount) {
      mappedEvents.push({
        id: 101,
        title: "Instant Payment Processed",
        time: new Date(shift.clockInOut?.end ?? shift.createdAt ?? ""),
        description: `$${paidAmount} paid instantly to worker`,
        icon: <DollarIcon sx={{ color: "#2e7d32" }} />,
        type: "payment",
        details: {
          amount: paidAmount,
          isPaidInstantly: true,
        },
      });
    }
  }

  // Process all shift logs
  shiftLogs.forEach((log, index) => {
    const event = getTimelineEventFromLog(log, index + 10);
    if (event) {
      mappedEvents.push(event);
    }
  });

  // Sort events by time
  mappedEvents.sort((a, b) => a.time.getTime() - b.time.getTime());

  const eventsWithIds = mappedEvents.map((event, idx) => ({
    ...event,
    id: idx,
  }));

  return eventsWithIds;
};

interface ShiftTimelinePageProps {
  shift: Shift;
}

export function ShiftTimelinePage({ shift }: ShiftTimelinePageProps) {
  const shiftId = shift._id ?? "";
  const {
    data: shiftResponse,
    isLoading,
    error,
  } = useGetShifts(
    {
      filter: {
        id: [shiftId],
      },
      fields: {
        shift: [
          "schedule.startAt",
          "schedule.endAt",
          "schedule.timeSlot",
          "schedule.extendBookabilityTo",
          "schedule.durationWithoutBreaksInHours",
          "requirements.qualificationName",
          "pay.total",
          "refillInfo.urgencyLevel",
          "refillInfo.previousWorker",
          "refillInfo.isUrgentlyBooked",
          "refillInfo.isMagicShift",
          "createdAt",
        ],
        shiftLog: [
          "actionDetails.action",
          "actionDetails.subAction",
          "actionDetails.reason",
          "actionDetails.notes",
          "actionDetails.performedBy",
          "leadTime",
          "pay",
          "eventAt",
          "expectedArrival",
          "clockInAt",
          "workerType",
          "workerName",
        ],
        worker: ["name"],
        workplace: ["timezone", "name"],
      },
      include: [API_TYPES.shiftLog, API_TYPES.worker, API_TYPES.workplace],
    },
    {
      enabled: isDefined(shiftId),
    }
  );

  const shiftLogs: ShiftLogSchema[] =
    shiftResponse?.included?.filter((included) => included.type === API_TYPES.shiftLog) ?? [];
  const events = mapShiftDataToTimelineEvents(shift, shiftLogs);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Text>Loading shift timeline...</Text>
      </Box>
    );
  }

  if (error) {
    const axiosError = error as import("axios").AxiosError;
    return (
      <Box
        sx={{
          padding: 3,
          bgcolor: "#ffebee",
          borderRadius: 1,
          color: "#d32f2f",
        }}
      >
        <Text bold>Error loading shift timeline</Text>
        <Text>{axiosError.message}</Text>
      </Box>
    );
  }

  if (!shift) {
    return <Text>Shift not found.</Text>;
  }

  return (
    <Box sx={{ minHeight: "100vh", padding: 2 }}>
      <Paper
        sx={{
          marginBottom: 2,
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Title bold component="h1">
          Shift Details
        </Title>
      </Paper>

      <Box sx={{ maxWidth: "1200px", margin: "0 auto" }}>
        <ShiftSummary shift={shift} />
        <Grid container spacing={2}>
          <ShiftTimeline events={events} />
          <Grid item xs={12} lg={4}>
            <Stack spacing={2}>
              <KeyMetricsAccordion shift={shift} events={events} />
              <ShiftMoreDetailsAccordion shift={shift} />
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
